import { type GetPortfolioLabChartItem, PortfolioLabContext } from "$root/functional-areas/portfolio-lab/ctx";
import { useLabSession } from "$root/functional-areas/portfolio-lab/hooks/useLabSession";
import { allowedMetrics, metricsMap } from "$root/widgets-architecture/widgets/PortfolioPerformance/common";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { ColumnState } from "ag-grid-community";
import { useState } from "react";
import { ColumnSettingsPanel } from "../shared/ColumnSettingsPanel";

type ColumnPreference = { colId: string; hide: boolean };

/** Derive the metric column preferences from the lab-session PERFORMANCE chart's persisted column state. */
function readColumnPreferences(storedColumns: ColumnState[]): ColumnPreference[] {
	const metricSet = new Set<string>(allowedMetrics);

	// Metric columns in stored order (preserves user reordering)
	const storedMetrics: ColumnPreference[] = storedColumns
		.filter((c) => metricSet.has(c.colId))
		.map((c) => ({ colId: c.colId, hide: c.hide ?? false }));
	const base = storedMetrics.length > 0 ? storedMetrics : allowedMetrics.map((m) => ({ colId: m, hide: false }));

	// Append any metric not yet stored (e.g. newly added metrics)
	const storedIds = new Set(base.map((m) => m.colId));
	const missing = allowedMetrics.filter((m) => !storedIds.has(m)).map((m) => ({ colId: m, hide: false }));

	return [...base, ...missing];
}

export function PerformanceChartSettings() {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const { saveLabSession } = useLabSession();

	const [columnPreferences, setColumnPreferences] = useState<ColumnPreference[]>(() => {
		const performanceChart = readAtom(ctx.charts$).find(
			(c): c is GetPortfolioLabChartItem<"PERFORMANCE"> => c.type === "PERFORMANCE",
		);
		return readColumnPreferences(performanceChart?.preference.columns ?? []);
	});

	const columnState = columnPreferences.map((pref) => ({
		colId: pref.colId,
		hide: pref.hide,
		label: metricsMap[pref.colId as keyof typeof metricsMap]?.label ?? pref.colId,
	}));

	function handleChange(newPreferences: ColumnPreference[]) {
		setColumnPreferences(newPreferences);
		// Persist metric columns only; the bridge atom in PerformanceChartInner re-applies them to the grid.
		const newState: ColumnState[] = newPreferences.map((p) => ({ colId: p.colId, hide: p.hide }));
		writeAtom(ctx.charts$, (cs) =>
			cs.map((c) => (c.type === "PERFORMANCE" ? { ...c, preference: { ...c.preference, columns: newState } } : c)),
		);
		saveLabSession();
	}

	return (
		<ColumnSettingsPanel
			title="Performance Chart"
			description="Manage column visibility and order"
			columnState={columnState}
			onChange={handleChange}
		/>
	);
}
